import { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Cloud,
  Compass,
  Globe,
  Layers,
  Lightbulb,
  Mail,
  MapPin,
  Menu,
  Phone,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  X,
  Zap,
  Rocket,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Service data with detailed content
const servicesData = [
  {
    id: "strategy",
    icon: Target,
    title: "IT Strategy & Consulting",
    shortDesc:
      "Strategic technology roadmaps and digital transformation guidance aligned with your business objectives.",
    features: [
      "Digital Transformation",
      "Technology Roadmapping",
      "IT Governance",
      "Process Optimization",
    ],
    fullDescription: `In today's rapidly evolving digital landscape, having a clear technology strategy is essential for business success. Our IT Strategy & Consulting services help you navigate complexity and make informed decisions that drive growth.

We work closely with your leadership team to understand your business goals, assess your current technology landscape, and develop a comprehensive roadmap that aligns technology investments with strategic objectives.

Our approach combines industry best practices with innovative thinking to deliver strategies that are both practical and forward-looking.`,
    offerings: [
      {
        title: "Digital Transformation Strategy",
        description:
          "Comprehensive assessment and roadmap for transforming your business through technology. We identify opportunities for automation, digitization, and innovation across your operations.",
      },
      {
        title: "Technology Roadmapping",
        description:
          "Multi-year technology plans that balance immediate needs with long-term vision. Prioritize investments and sequence initiatives for maximum impact.",
      },
      {
        title: "IT Governance Framework",
        description:
          "Establish clear decision-making processes, policies, and standards that ensure technology serves business needs while managing risk.",
      },
      {
        title: "Process Optimization",
        description:
          "Analyze and reengineer business processes to leverage technology effectively, eliminate inefficiencies, and improve outcomes.",
      },
    ],
    benefits: [
      "Aligned technology investments",
      "Reduced operational risk",
      "Improved decision-making",
      "Competitive advantage",
    ],
  },
  {
    id: "enterprise",
    icon: Layers,
    title: "Enterprise Platforms",
    shortDesc:
      "End-to-end enterprise platform implementation and integration, from CRM and ERP to custom business applications.",
    features: [
      "CRM Implementation",
      "ERP Integration",
      "Platform Migration",
      "System Integration",
    ],
    fullDescription: `Modern enterprises rely on powerful platforms to manage customer relationships, operations, and data. Our Enterprise Platform services ensure you get the most from your technology investments.

From selecting the right platform to seamless implementation and integration, we guide you through every step of the journey. Our team has deep expertise across leading enterprise platforms including Salesforce, Microsoft Dynamics, SAP, and custom solutions.

We focus on delivering solutions that not only meet your current needs but scale with your business growth.`,
    offerings: [
      {
        title: "CRM Implementation",
        description:
          "Deploy and customize customer relationship management platforms that help you understand, engage, and retain customers more effectively.",
      },
      {
        title: "ERP Integration",
        description:
          "Unify your business processes with enterprise resource planning solutions that connect finance, operations, supply chain, and human resources.",
      },
      {
        title: "Platform Migration",
        description:
          "Seamlessly transition from legacy systems to modern platforms with minimal disruption to your business operations.",
      },
      {
        title: "System Integration",
        description:
          "Connect disparate systems and applications to create a unified technology ecosystem that shares data and workflows efficiently.",
      },
    ],
    benefits: [
      "Unified data across systems",
      "Streamlined operations",
      "Improved productivity",
      "Scalable architecture",
    ],
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Cloud & Integrations Engineering",
    shortDesc:
      "Seamless cloud architecture, migration, and integration solutions that unlock the full potential of cloud-native technologies.",
    features: ["Cloud Architecture", "API Development", "Data Integration", "Microservices"],
    fullDescription: `Cloud computing has transformed how businesses build, deploy, and scale applications. Our Cloud & Integrations Engineering services help you harness the full power of the cloud.

Whether you're migrating existing workloads, building cloud-native applications, or connecting systems through APIs, our team brings deep expertise across AWS, Azure, and Google Cloud platforms.

We design architectures that are secure, scalable, and cost-effective, ensuring your cloud investments deliver maximum value.`,
    offerings: [
      {
        title: "Cloud Architecture Design",
        description:
          "Design robust, scalable cloud infrastructures that support your application needs while optimizing costs and ensuring security.",
      },
      {
        title: "API Development",
        description:
          "Build secure, well-documented APIs that enable seamless integration between systems and support your digital ecosystem.",
      },
      {
        title: "Data Integration",
        description:
          "Unify data from multiple sources to create a single source of truth that powers analytics and business intelligence.",
      },
      {
        title: "Microservices Development",
        description:
          "Build modular, independently deployable services that enable faster development cycles and improved system resilience.",
      },
    ],
    benefits: ["Increased agility", "Reduced infrastructure costs", "Improved reliability", "Faster time-to-market"],
  },
  {
    id: "quality",
    icon: Shield,
    title: "Quality Engineering",
    shortDesc:
      "Comprehensive testing and quality assurance services that ensure your digital products perform flawlessly.",
    features: ["Test Automation", "Performance Testing", "Security Testing", "CI/CD Integration"],
    fullDescription: `Quality is not an afterthought—it's built into every stage of development. Our Quality Engineering services ensure your applications meet the highest standards of reliability, performance, and security.

We employ modern testing practices including test automation, continuous testing, and shift-left approaches to catch issues early and reduce the cost of defects.

From functional testing to performance benchmarking and security assessments, we provide comprehensive quality coverage that gives you confidence in your releases.`,
    offerings: [
      {
        title: "Test Automation",
        description:
          "Build robust automated test suites that provide rapid feedback on code quality and free your team to focus on higher-value testing activities.",
      },
      {
        title: "Performance Testing",
        description:
          "Validate that your applications perform under expected and peak loads, identifying bottlenecks before they impact users.",
      },
      {
        title: "CI/CD Integration",
        description:
          "Embed quality checks into your continuous integration and delivery pipelines for automated quality gates and faster releases.",
      },
    ],
    benefits: ["Fewer production defects", "Faster release cycles", "Improved user satisfaction", "Reduced testing costs"],
  },
  {
    id: "digital",
    icon: Globe,
    title: "Web & Digital Experience",
    shortDesc:
      "Stunning, high-performance websites and digital experiences that captivate users and drive engagement.",
    features: ["Web Development", "UX/UI Design", "E-commerce", "Progressive Web Apps"],
    fullDescription: `Your digital presence is often the first impression customers have of your brand. Our Web & Digital Experience services help you make that impression count.

We combine beautiful design with cutting-edge technology to create websites and applications that engage users, communicate your value proposition, and drive conversions.

From corporate websites to complex web applications and e-commerce platforms, we deliver experiences that set you apart from the competition.`,
    offerings: [
      {
        title: "Web Development",
        description:
          "Build fast, responsive websites using modern technologies that deliver exceptional performance and user experiences across all devices.",
      },
      {
        title: "UX/UI Design",
        description:
          "Create intuitive, beautiful interfaces through user-centered design processes that prioritize usability and aesthetic appeal.",
      },
      {
        title: "E-commerce Solutions",
        description:
          "Launch and optimize online stores that convert visitors into customers with seamless shopping experiences and robust backend systems.",
      },
      {
        title: "Progressive Web Apps",
        description:
          "Deliver app-like experiences through the web with offline capabilities, push notifications, and installable applications.",
      },
    ],
    benefits: ["Increased engagement", "Higher conversion rates", "Improved brand perception", "Better SEO performance"],
  },
];

// Approach process data
const approachData = [
  {
    id: "discover",
    step: "01",
    title: "Discover",
    shortDesc: "Understand your business, goals, and digital challenges",
    icon: Compass,
    image: "/process-discover.png",
    fullDescription:
      "The discovery phase is where we build a deep understanding of your business, challenges, and aspirations. Through structured workshops, stakeholder interviews, and system analysis, we gather the insights needed to craft the right solution.",
    activities: [
      {
        title: "Business Analysis",
        description:
          "We analyze your current business model, processes, and pain points to identify opportunities for digital improvement. This includes reviewing existing systems, workflows, and performance metrics.",
      },
      {
        title: "Stakeholder Interviews",
        description:
          "One-on-one conversations with key stakeholders across your organization to understand diverse perspectives, requirements, and success criteria.",
      },
      {
        title: "Requirements Gathering",
        description:
          "Document functional and non-functional requirements that will guide solution design, including user needs, technical constraints, and business rules.",
      },
      {
        title: "Feasibility Study",
        description:
          "Assess technical and business feasibility of proposed solutions, identifying risks, dependencies, and success factors.",
      },
    ],
    deliverables: ["Business Requirements Document", "Current State Assessment", "Risk Analysis", "Project Scope Definition"],
  },
  {
    id: "design",
    step: "02",
    title: "Design",
    shortDesc: "Craft tailored solutions aligned with your objectives",
    icon: Lightbulb,
    image: "/process-design.png",
    fullDescription:
      "In the design phase, we transform insights from discovery into concrete solution architectures and plans. Our design process balances innovation with practicality, ensuring solutions are both visionary and implementable.",
    activities: [
      {
        title: "Solution Architecture",
        description:
          "Design the overall system architecture including technology stack, integration patterns, security model, and scalability approach.",
      },
      {
        title: "UX/UI Wireframes",
        description:
          "Create user experience flows and interface designs that prioritize usability, accessibility, and visual appeal.",
      },
      {
        title: "Technology Stack Selection",
        description:
          "Evaluate and select the optimal combination of technologies, platforms, and tools based on requirements, budget, and long-term maintainability.",
      },
      {
        title: "Implementation Roadmap",
        description:
          "Develop a phased implementation plan with clear milestones, dependencies, and resource requirements.",
      },
    ],
    deliverables: ["Solution Architecture Document", "UX/UI Designs", "Technical Specifications", "Project Plan"],
  },
  {
    id: "deliver",
    step: "03",
    title: "Deliver",
    shortDesc: "Execute with precision using agile methodologies",
    icon: Rocket,
    image: "/process-deliver.png",
    fullDescription:
      "Delivery is where plans become reality. We use agile methodologies to build solutions iteratively, with regular checkpoints and continuous feedback to ensure we stay aligned with your expectations.",
    activities: [
      {
        title: "Development Sprints",
        description:
          "Work in time-boxed iterations to build functionality incrementally, with regular demos and opportunities for feedback and course correction.",
      },
      {
        title: "Quality Assurance",
        description:
          "Comprehensive testing at every stage including unit tests, integration tests, and user acceptance testing to ensure high quality.",
      },
      {
        title: "User Acceptance Testing",
        description:
          "Collaborate with your team to validate that the solution meets requirements and performs as expected in real-world scenarios.",
      },
      {
        title: "Deployment",
        description:
          "Plan and execute production deployment with minimal disruption, including rollback strategies and post-deployment monitoring.",
      },
    ],
    deliverables: ["Working Software", "Test Reports", "User Documentation", "Deployment Artifacts"],
  },
  {
    id: "optimize",
    step: "04",
    title: "Optimize",
    shortDesc: "Continuously improve and support your digital assets",
    icon: TrendingUp,
    image: "/process-optimize.png",
    fullDescription:
      "Launch is just the beginning. Our optimization phase ensures your solution continues to deliver value through ongoing monitoring, support, and iterative improvements based on real-world usage and feedback.",
    activities: [
      {
        title: "Performance Monitoring",
        description:
          "Track system performance, user behavior, and business metrics to identify opportunities for improvement and ensure service levels are met.",
      },
      {
        title: "Feedback Collection",
        description:
          "Gather structured feedback from users and stakeholders to understand what's working well and where enhancements are needed.",
      },
      {
        title: "Optimization Iterations",
        description:
          "Implement continuous improvements based on data and feedback, prioritizing changes that deliver the most value.",
      },
      {
        title: "Enhancement Releases",
        description:
          "Plan and deliver new features and capabilities that extend the solution's value and keep pace with evolving business needs.",
      },
    ],
    deliverables: ["Performance Reports", "Enhancement Roadmap", "Support Documentation", "Training Materials"],
  },
];

// Form data interface
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  service: string;
  message: string;
}

// API endpoint - works for both local dev and Netlify production
// const API_ENDPOINT = "/api/send-message";
const API_ENDPOINT = "/.netlify/functions/send-message";

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [selectedService, setSelectedService] = useState<(typeof servicesData)[number] | null>(null);
  const [selectedApproach, setSelectedApproach] = useState<(typeof approachData)[number] | null>(null);

  // Contact form state
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<null | "success" | "error">(null);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ["home", "services", "about", "approach", "contact"];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom >= 100) {
          setActiveSection(section);
          break;
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  // Form handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Send message to Google Sheets via Netlify Function
  const sendMessage = async (): Promise<void> => {
    const { firstName, lastName, email, company, service, message } = formData;

    // Validation
    if (!firstName || !email || !message) {
      setStatus("error");
      setStatusMessage("Please fill in all required fields (First Name, Email, Message).");
      return;
    }

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus("error");
      setStatusMessage("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setStatusMessage("");

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          company,
          service,
          message,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("success");
        setStatusMessage("Thank you! Your message has been received. We will contact you shortly.");

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          service: "",
          message: "",
        });
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Send error:", error);
      setStatus("error");
      setStatusMessage("Sorry, there was an error sending your message. Please try again or contact us directly at info@buildyourdreamz.com.au");
    } finally {
      setIsSubmitting(false);
    }
  };

  const navItems = [
    { id: "home", label: "Home" },
    { id: "services", label: "Services" },
    { id: "about", label: "About" },
    { id: "approach", label: "Approach" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "backdrop-blur-xl bg-background/90 border-b border-border/50 shadow-lg shadow-cyan-500/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button
              type="button"
              onClick={() => scrollToSection("home")}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              aria-label="Go to Home"
            >
              <img src="/logo.png" alt="Build Your Dreamz" className="h-12 w-auto" />
            </button>

            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToSection(item.id)}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                    activeSection === item.id
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                  }`}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-gradient-brand rounded-full" />
                  )}
                </button>
              ))}
            </div>

            <div className="hidden md:block">
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-gradient-brand hover:bg-gradient-brand-hover text-white font-semibold shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-500/40"
              >
                Get Free Consultation
              </Button>
            </div>

            <button
              type="button"
              className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="backdrop-blur-xl bg-background/95 border-b border-border px-4 py-6 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                className={`block w-full text-left py-3 px-4 rounded-lg transition-colors ${
                  activeSection === item.id
                    ? "text-primary bg-primary/10 font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`}
              >
                {item.label}
              </button>
            ))}
            <Button
              onClick={() => scrollToSection("contact")}
              className="w-full bg-gradient-brand text-white font-semibold mt-4 py-6"
            >
              Get Free Consultation
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-bg.jpg" alt="Background" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/80 to-background" />
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass-effect mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Build Your Dreamz</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8">
              Digital Solutions That
              <span className="block text-gradient mt-2">Elevate Your Business</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              From strategy to execution, we craft digital experiences and enterprise solutions that drive innovation,
              streamline operations, and accelerate growth in the digital era.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => scrollToSection("contact")}
                className="bg-gradient-brand hover:bg-gradient-brand-hover text-white font-semibold text-lg px-8 py-6 shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-500/40 hover:scale-105"
              >
                Start Your Digital Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection("services")}
                className="border-white/20 hover:bg-white/10 text-lg px-8 py-6 backdrop-blur-sm"
              >
                Explore Our Services
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-gradient-brand rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary font-semibold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 mb-4">
              Our <span className="text-gradient">Digital Services</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive digital solutions designed to transform your business and deliver exceptional experiences
              across every touchpoint.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesData.map((service) => (
              <Card
                key={service.id}
                className="group glass-effect border-0 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-gradient-brand flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">{service.shortDesc}</p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant="outline"
                    className="w-full border-primary/30 hover:bg-primary/10 hover:border-primary transition-all"
                    onClick={() => setSelectedService(service)}
                  >
                    Learn More
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      <Dialog
        open={!!selectedService}
        onOpenChange={(open) => {
          if (!open) setSelectedService(null);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-brand flex items-center justify-center">
                    <selectedService.icon className="w-7 h-7 text-white" />
                  </div>
                  <DialogTitle className="text-2xl font-bold">{selectedService.title}</DialogTitle>
                </div>

                <DialogDescription className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
                  {selectedService.fullDescription}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 space-y-8">
                <div>
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    What We Offer
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {selectedService.offerings.map((offering) => (
                      <div key={offering.title} className="glass-effect rounded-xl p-4">
                        <h5 className="font-semibold mb-2 text-primary">{offering.title}</h5>
                        <p className="text-sm text-muted-foreground">{offering.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Key Benefits
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedService.benefits.map((benefit) => (
                      <span
                        key={benefit}
                        className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium"
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center pt-4">
                  <Button
                    onClick={() => {
                      setSelectedService(null);
                      scrollToSection("contact");
                    }}
                    className="bg-gradient-brand hover:bg-gradient-brand-hover text-white font-semibold px-8 py-6"
                  >
                    Discuss Your Project
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* About */}
      <section id="about" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-teal-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">Who We Are</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 mb-6">
                Building Digital <span className="text-gradient">Dreamz Into Reality</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                At Build Your Dreamz, we believe every business deserves digital excellence. Founded with a vision to
                bridge the gap between business goals and technology, we&apos;ve grown into a trusted partner for
                organizations seeking transformative digital solutions.
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Our team combines deep technical expertise with strategic business acumen. We don&apos;t just implement
                technology — we craft digital strategies that align with your vision, engage your customers, and drive
                measurable business outcomes.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Users, label: "Expert Consultants", value: "Certified Professionals" },
                  { icon: Globe, label: "Coverage", value: "Australia Wide" },
                  { icon: Zap, label: "Approach", value: "Agile & Adaptive" },
                  { icon: Target, label: "Focus", value: "Results Driven" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">{item.label}</div>
                      <div className="text-sm text-muted-foreground">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-effect rounded-2xl p-8">
              <img
                src="/about-team.jpg"
                alt="Build Your Dreamz team"
                className="w-full h-72 object-cover rounded-xl mb-6"
              />
              <h3 className="text-2xl font-bold mb-3">Senior-led, quality-first delivery</h3>
              <p className="text-muted-foreground leading-relaxed">
                Strategy, engineering, and quality working together—so your solutions are scalable, secure, and
                production-ready.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section id="approach" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary font-semibold text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 mb-4">
              Our <span className="text-gradient">Engagement Approach</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A proven methodology that ensures every project delivers exceptional results through structured discovery,
              design, delivery, and optimization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachData.map((approach, index) => (
              <div key={approach.id} className="relative group">
                <div className="glass-effect rounded-2xl p-6 text-center transition-all duration-300 hover:bg-white/10 hover:-translate-y-2 h-full flex flex-col">
                  <div className="text-4xl font-bold text-gradient mb-4">{approach.step}</div>
                  <div className="w-12 h-12 rounded-full bg-gradient-brand flex items-center justify-center mx-auto mb-4">
                    <approach.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-lg">{approach.title}</h4>
                  <p className="text-sm text-muted-foreground mb-6 flex-grow">{approach.shortDesc}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-primary/30 hover:bg-primary/10 hover:border-primary transition-all"
                    onClick={() => setSelectedApproach(approach)}
                  >
                    Explore Phase
                    <ChevronRight className="ml-1 w-4 h-4" />
                  </Button>
                </div>

                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Detail Modal */}
      <Dialog
        open={!!selectedApproach}
        onOpenChange={(open) => {
          if (!open) setSelectedApproach(null);
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-card border-border">
          {selectedApproach && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-5xl font-bold text-gradient">{selectedApproach.step}</div>
                  <div>
                    <DialogTitle className="text-2xl font-bold">{selectedApproach.title} Phase</DialogTitle>
                  </div>
                </div>
                <DialogDescription className="text-lg text-muted-foreground leading-relaxed">
                  {selectedApproach.fullDescription}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 rounded-xl overflow-hidden">
                <img src={selectedApproach.image} alt={`${selectedApproach.title} Process`} className="w-full h-auto" />
              </div>

              <div className="mt-8 space-y-8">
                <div>
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Key Activities
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {selectedApproach.activities.map((activity) => (
                      <div key={activity.title} className="glass-effect rounded-xl p-4">
                        <h5 className="font-semibold mb-2">{activity.title}</h5>
                        <p className="text-sm text-muted-foreground">{activity.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    Deliverables
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedApproach.deliverables.map((deliverable) => (
                      <span
                        key={deliverable}
                        className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium"
                      >
                        {deliverable}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center pt-4">
                  <Button
                    onClick={() => {
                      setSelectedApproach(null);
                      scrollToSection("contact");
                    }}
                    className="bg-gradient-brand hover:bg-gradient-brand-hover text-white font-semibold px-8 py-6"
                  >
                    Start Your Project
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-brand opacity-10" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Digital Presence?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Let&apos;s discuss how we can help you achieve your digital goals. Your first consultation is completely free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => scrollToSection("contact")}
              className="bg-gradient-brand hover:bg-gradient-brand-hover text-white font-semibold text-lg px-8 py-6 shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-500/40 hover:scale-105"
            >
              Book Your Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6">Let&apos;s explore the possibilities together.</p>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 mb-6">
                Let&apos;s Build Something <span className="text-gradient">Amazing Together</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Whether you have a specific project in mind or want to explore digital possibilities, we&apos;re here to help.
                Reach out and start your digital transformation journey.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Call Us</div>
                    <div className="font-semibold">+61 485 040 048</div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Email Us</div>
                    <div className="font-semibold">info@buildyourdreamz.com.au</div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Visit Us</div>
                    <div className="font-semibold">Melbourne, Australia</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Send Us a Message</h3>

              {status === "error" && (
                <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500">
                  {statusMessage}
                </div>
              )}

              {status === "success" && (
                <div className="mb-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-500">
                  {statusMessage}
                </div>
              )}

              <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="firstName">
                      First Name *
                    </label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      className="bg-background/50 border-white/10"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" htmlFor="lastName">
                      Last Name
                    </label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                      className="bg-background/50 border-white/10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="email">
                    Email *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="bg-background/50 border-white/10"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="company">
                    Company
                  </label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Name"
                    className="bg-background/50 border-white/10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="service">
                    Service Interest
                  </label>
                  <select
                    id="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full h-10 px-3 rounded-md bg-background/50 border border-white/10 text-sm"
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    <option value="IT Strategy & Consulting">IT Strategy & Consulting</option>
                    <option value="Enterprise Platforms">Enterprise Platforms</option>
                    <option value="Cloud & Integrations Engineering">Cloud & Integrations Engineering</option>
                    <option value="Quality Engineering">Quality Engineering</option>
                    <option value="Web & Digital Experience">Web & Digital Experience</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" htmlFor="message">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project or digital challenges..."
                    className="bg-background/50 border-white/10 min-h-[120px]"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-brand hover:bg-gradient-brand-hover text-white font-semibold py-6 transition-all hover:shadow-lg hover:shadow-cyan-500/25 disabled:opacity-50"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <img src="/logo.png" alt="Build Your Dreamz" className="h-12 w-auto mb-4" />
              <p className="text-muted-foreground max-w-sm">
                Digital transformation experts helping businesses build exceptional digital experiences.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>IT Strategy & Consulting</li>
                <li>Enterprise Platforms</li>
                <li>Cloud & Integrations</li>
                <li>Quality Engineering</li>
                <li>Web & Digital Experience</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">© 2026 Build Your Dreamz. All rights reserved.</p>
            <p className="text-sm text-muted-foreground">ABN: 25 373 067 151 | buildyourdreamz.com.au</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
